import React from 'react';
import { Badge } from 'flowbite-react';
import type { DecisionFrame } from '../../api/workflowInspector';
import { getConfidenceColor } from '../../api/workflowInspector';

interface DecisionFrameDetailProps {
  frame: DecisionFrame;
}

const DecisionFrameDetail: React.FC<DecisionFrameDetailProps> = ({ frame }) => {
  return (
    <div className="mt-3 space-y-4 border-t border-gray-200 pt-3">
      {/* Why This Agent */}
      <div>
        <h5 className="text-xs font-semibold text-gray-500 uppercase mb-1">Why This Agent?</h5>
        <p className="text-sm text-gray-700">{frame.Selection_Reason || 'No reason recorded'}</p>
        <div className="mt-1 flex items-center gap-2">
          <span className="text-xs text-gray-500">Confidence:</span>
          <Badge color={getConfidenceColor(frame.Confidence_Level)} size="xs">
            {frame.Confidence_Level}
            {frame.Confidence !== null && ` (${Math.round(frame.Confidence * 100)}%)`}
          </Badge>
          <span className="text-xs text-gray-400">Routed by: {frame.Routed_By}</span>
        </div>
      </div>

      {/* Score Breakdown */}
      {Object.keys(frame.Score_Breakdown).length > 0 && (
        <div>
          <h5 className="text-xs font-semibold text-gray-500 uppercase mb-1">Score Breakdown</h5>
          <div className="flex flex-wrap gap-2">
            {Object.entries(frame.Score_Breakdown).map(([key, value]) => (
              <span key={key} className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-700">
                {key}: <span className="font-medium ml-1">{value}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Alternatives Considered */}
      {frame.Fallbacks.length > 0 && (
        <div>
          <h5 className="text-xs font-semibold text-gray-500 uppercase mb-1">Alternatives Considered</h5>
          <div className="space-y-1">
            {frame.Fallbacks.map((fb, i) => (
              <div key={i} className="flex items-center justify-between text-sm bg-gray-50 rounded px-2 py-1">
                <span className="text-gray-700">{fb.agent}</span>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">Score: {fb.score}</span>
                  {fb.reason && <span className="text-xs text-gray-400">{fb.reason}</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Constraints Violated */}
      {frame.Constraints_Violated.length > 0 && (
        <div>
          <h5 className="text-xs font-semibold text-gray-500 uppercase mb-1">Constraints Violated</h5>
          <div className="space-y-1">
            {frame.Constraints_Violated.map((v, i) => (
              <div key={i} className="bg-red-50 border border-red-200 rounded px-3 py-2">
                <div className="flex items-center gap-2">
                  <Badge color="failure" size="xs">{v.tier}</Badge>
                  <span className="text-sm font-medium text-red-800">{v.invariant}</span>
                </div>
                <p className="text-xs text-red-600 mt-1">{v.reason}</p>
                {v.context && <p className="text-xs text-red-400 mt-0.5">{v.context}</p>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Failover History */}
      {frame.Failover_History.length > 0 && (
        <div>
          <h5 className="text-xs font-semibold text-gray-500 uppercase mb-1">Failover History</h5>
          <div className="space-y-1">
            {frame.Failover_History.map((fh, i) => (
              <div key={i} className="bg-yellow-50 border border-yellow-200 rounded px-3 py-2">
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-gray-600">{fh.originalAgent}</span>
                  <span className="text-gray-400">&rarr;</span>
                  <span className="font-medium text-gray-800">{fh.fallbackAgent}</span>
                  <Badge color={fh.success ? 'success' : 'failure'} size="xs">
                    {fh.success ? 'OK' : 'Failed'}
                  </Badge>
                </div>
                <p className="text-xs text-yellow-700 mt-1">
                  Strategy: {fh.strategy} &mdash; {fh.reason}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Evidence Gaps */}
      {frame.Evidence_Gaps.length > 0 && (
        <div>
          <h5 className="text-xs font-semibold text-gray-500 uppercase mb-1">Evidence Gaps</h5>
          <ul className="list-disc list-inside text-sm text-gray-600 space-y-0.5">
            {frame.Evidence_Gaps.map((gap, i) => (
              <li key={i}>
                {typeof gap === 'string' ? gap : `${gap.field}: ${gap.reason}`}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default DecisionFrameDetail;
